import { FC, Fragment } from "react";
import { Platform, StyleSheet } from "react-native";
import * as TooltipPrimitive from "@rn-primitives/tooltip";
import { FadeInDown, FadeInUp, FadeOut } from "react-native-reanimated";
import { FullWindowOverlay as RNFullWindowOverlay } from "react-native-screens";

import { TextClassContext } from "@/context/TextClassContext";
import { usePlatformOS } from "@/hooks/usePlatformOS";
import { ChildrenOrI18nType } from "@/i18n";
import { cn } from "@/utils/tailwind";

import { VnThemedBox } from "./VnBox";
import { VnNativeOnlyAnimatedView } from "./VnNativeOnlyAnimatedView";
import { VnText } from "./VnText/VnText";

export const VnTooltip = TooltipPrimitive.Root;

export const VnTooltipTrigger = TooltipPrimitive.Trigger;

const FullWindowOverlay =
  Platform.OS === "ios" ? RNFullWindowOverlay : Fragment;

type VnTooltipContentPropsType = TooltipPrimitive.ContentProps &
  ChildrenOrI18nType & {
    portalHost?: string;
  };

export const VnTooltipContent: FC<VnTooltipContentPropsType> = ({
  className,
  sideOffset = 4,
  portalHost,
  side = "top",
  children,
  i18nKey,
  i18nOptions,
  ...props
}) => {
  const { isWeb, isNative } = usePlatformOS();

  return (
    <TooltipPrimitive.Portal hostName={portalHost}>
      <FullWindowOverlay>
        <TooltipPrimitive.Overlay
          style={isNative ? StyleSheet.absoluteFill : undefined}>
          <VnThemedBox>
            <VnNativeOnlyAnimatedView
              entering={
                side === "top"
                  ? FadeInDown.withInitialValues({
                      transform: [{ translateY: 3 }],
                    }).duration(150)
                  : FadeInUp.withInitialValues({
                      transform: [{ translateY: -5 }],
                    })
              }
              exiting={FadeOut}>
              <TextClassContext.Provider value="text-xs text-primary-foreground">
                <TooltipPrimitive.Content
                  sideOffset={sideOffset}
                  className={cn(
                    "bg-primary z-50 rounded-md px-3 py-2 sm:py-1.5",
                    isWeb &&
                      cn(
                        "origin-(--radix-tooltip-content-transform-origin) animate-in fade-in-0 zoom-in-95 w-fit text-balance",
                        side === "bottom" && "slide-in-from-top-2",
                        side === "left" && "slide-in-from-right-2",
                        side === "right" && "slide-in-from-left-2",
                        side === "top" && "slide-in-from-bottom-2",
                      ),
                    className,
                  )}
                  side={side}
                  {...props}>
                  {children ?? (
                    <VnText
                      variant="sm"
                      i18nKey={i18nKey}
                      i18nOptions={i18nOptions}
                    />
                  )}
                </TooltipPrimitive.Content>
              </TextClassContext.Provider>
            </VnNativeOnlyAnimatedView>
          </VnThemedBox>
        </TooltipPrimitive.Overlay>
      </FullWindowOverlay>
    </TooltipPrimitive.Portal>
  );
};
